import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { ADD_USER, UPDATE_USER } from "../../../../utils/graphql/users/mutations";
import { UPDATE_MEETING } from "../../../../utils/graphql/meetings/mutations";
import { QUERY_MEETINGS, GET_MEETINGS_FOR_USER } from "../../../../utils/graphql/meetings/queries";
import { ModalForm } from "../../../../components/Modal/ModalForm";
import { UserAttributeEditor } from "../../../../components/user/UserAttributeEditor";

// Staged meeting assignment editor — no mutations fire here.
// pendingAssignments: array of { meeting, role } objects representing the current intended state.
// onChange(newAssignments): called whenever the user adds or removes an assignment.
function MeetingAssignmentEditor({ userId, allMeetings, loadingAll, pendingAssignments, onChange }) {
  const [selectedRole, setSelectedRole] = useState("host");
  const [selectedMeetingId, setSelectedMeetingId] = useState("");

  const getRoleLabel = (role) => {
    if (role === "host") return "Host";
    if (role === "coHost") return "Co-Host";
    if (role === "alternateHost") return "Alternate Host";
    return role;
  };

  const assignedMeetingIds = new Set(pendingAssignments.map(a => a.meeting._id));
  const unassignedMeetings = allMeetings.filter(m => !assignedMeetingIds.has(m._id));

  const handleAdd = () => {
    if (!selectedMeetingId) return;
    const meeting = allMeetings.find(m => m._id === selectedMeetingId);
    if (!meeting) return;
    onChange([...pendingAssignments, { meeting, role: selectedRole }]);
    setSelectedMeetingId("");
  };

  const handleRemove = (meetingId) => {
    onChange(pendingAssignments.filter(a => a.meeting._id !== meetingId));
  };

  if (loadingAll) return <div className="text-muted small">Loading meetings...</div>;

  return (
    <div className="mt-3">
      <h6 className="fw-semibold">Meeting Assignments</h6>

      {pendingAssignments.length === 0 ? (
        <p className="text-muted small">Not assigned to any meetings.</p>
      ) : (
        <ul className="list-group mb-2">
          {pendingAssignments.map(({ meeting, role }) => (
            <li key={meeting._id} className="list-group-item d-flex justify-content-between align-items-center py-1 px-2">
              <span>
                <span className="me-2">{meeting.summary || "(untitled)"}</span>
                <span className="badge bg-secondary">{getRoleLabel(role)}</span>
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleRemove(meeting._id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {unassignedMeetings.length > 0 && (
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 240 }}
            value={selectedMeetingId}
            onChange={e => setSelectedMeetingId(e.target.value)}
          >
            <option value="">Select a meeting...</option>
            {unassignedMeetings.map(m => (
              <option key={m._id} value={m._id}>{m.summary || "(untitled)"}</option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 150 }}
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}
          >
            <option value="host">Host</option>
            <option value="coHost">Co-Host</option>
            <option value="alternateHost">Alternate Host</option>
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleAdd}
            disabled={!selectedMeetingId}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

function UserForm({
  mutation,
  initialData = { _id: "", username: "", email: "", admin: false, timezone: "", attributes: [] },
  onSuccess,
  onClose,
  buttonLabel = "Submit",
}) {
  const isEditing = !!initialData._id;

  // BASIC USER FIELDS
  const [formData, setFormData] = useState({
    username: initialData.username ?? "",
    email: initialData.email ?? "",
    admin: initialData.admin ?? false,
    timezone: initialData.timezone ?? "",
  });

  // HYDRATE EXISTING ATTRIBUTE VALUES
  const [values, setValues] = useState(() => {
    const map = {};
    initialData.attributes?.forEach(attr => {
      map[attr.key] = attr.value;
    });
    return map;
  });

  // MEETING ASSIGNMENT DATA (edit mode only)
  const { data: allMeetingsData, loading: loadingAllMeetings } = useQuery(QUERY_MEETINGS, {
    skip: !isEditing,
  });
  const { data: userMeetingsData, loading: loadingUserMeetings } = useQuery(GET_MEETINGS_FOR_USER, {
    variables: { userId: initialData._id },
    skip: !isEditing,
  });
  const [updateMeeting] = useMutation(UPDATE_MEETING, {
    refetchQueries: [
      { query: GET_MEETINGS_FOR_USER, variables: { userId: initialData._id } },
      { query: QUERY_MEETINGS },
    ],
  });

  const allMeetings = allMeetingsData?.meetings ?? [];

  // Initialise pending assignments once the user's current meetings load
  const [pendingAssignments, setPendingAssignments] = useState(null);
  React.useEffect(() => {
    if (!userMeetingsData) return;
    const assigned = userMeetingsData.meetingsForUser ?? [];
    setPendingAssignments(
      assigned.map(m => ({
        meeting: m,
        role: m.host?._id === initialData._id
          ? "host"
          : m.coHost?._id === initialData._id
          ? "coHost"
          : "alternateHost",
      }))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMeetingsData]);

  // UNIFIED MUTATION
  const [mutateUser, { loading, error }] = useMutation(mutation);

  // UPDATE BASIC FIELDS
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // MAIN SUBMIT HANDLER
  const handleSubmit = async e => {
    e.preventDefault();

    try {
      const attributePayload = Object.keys(values).map(key => ({
        key,
        value: values[key],
      }));

      const userVariables = {
        _id: initialData._id,
        username: formData.username,
        email: formData.email,
        admin: formData.admin,
        attributes: attributePayload,
      };

      if (isEditing) {
        userVariables.timezone = formData.timezone.trim();
      }

      await mutateUser({
        variables: userVariables
      });

      // Apply meeting assignment changes
      if (isEditing && pendingAssignments !== null) {
        const originalIds = new Set(
          (userMeetingsData?.meetingsForUser ?? []).map(m => m._id)
        );
        const pendingIds = new Set(pendingAssignments.map(a => a.meeting._id));

        // Removed — clear the user from their role(s) on that meeting
        const removed = (userMeetingsData?.meetingsForUser ?? []).filter(
          m => !pendingIds.has(m._id)
        );
        // Added — set the user in the chosen role on that meeting
        const added = pendingAssignments.filter(a => !originalIds.has(a.meeting._id));

        const meetingMutations = [
          ...removed.map(meeting => {
            const input = {
              hostId: meeting.host?._id === initialData._id ? null : (meeting.host?._id ?? null),
              coHostId: meeting.coHost?._id === initialData._id ? null : (meeting.coHost?._id ?? null),
              alternateHostId: meeting.alternateHost?._id === initialData._id ? null : (meeting.alternateHost?._id ?? null),
            };
            return updateMeeting({ variables: { id: meeting._id, input } });
          }),
          ...added.map(({ meeting, role }) => {
            const fullMeeting = allMeetings.find(m => m._id === meeting._id) ?? meeting;
            const input = {
              hostId: fullMeeting.host?._id ?? null,
              coHostId: fullMeeting.coHost?._id ?? null,
              alternateHostId: fullMeeting.alternateHost?._id ?? null,
            };
            input[`${role}Id`] = initialData._id;
            return updateMeeting({ variables: { id: meeting._id, input } });
          }),
        ];

        await Promise.all(meetingMutations);
      }

      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 border rounded bg-light">

      {/* BASIC FIELDS */}
      <div className="mb-2">
        <label className="form-label">Name</label>
        <input
          className="form-control"
          id="username"
          name="username"
          value={formData.username}
          onChange={handleChange}
          required
        />
      </div>

      <div className="mb-2">
        <label className="form-label">Email</label>
        <input
          className="form-control"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>

      {isEditing && (
        <div className="mb-2">
          <label className="form-label">Timezone</label>
          <input
            className="form-control"
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            placeholder="America/New_York"
            autoComplete="off"
          />
          <div className="form-text">
            Use an IANA timezone (example: America/Los_Angeles). Leave blank to clear.
          </div>
        </div>
      )}

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          name="admin"
          checked={formData.admin}
          onChange={handleChange}
        />
        <label className="form-check-label">Admin?</label>
      </div>

      {/* DYNAMIC USER ATTRIBUTES */}
      <UserAttributeEditor
        initialValues={values}
        onChange={setValues}
        onlyEditable
      />

      {/* MEETING ASSIGNMENTS (edit mode only) */}
      {isEditing && (
        <>
          <hr />
          {loadingUserMeetings || pendingAssignments === null ? (
            <div className="text-muted small">Loading meetings...</div>
          ) : (
            <MeetingAssignmentEditor
              userId={initialData._id}
              allMeetings={allMeetings}
              loadingAll={loadingAllMeetings}
              pendingAssignments={pendingAssignments}
              onChange={setPendingAssignments}
            />
          )}
          <hr />
        </>
      )}

      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? "Saving..." : buttonLabel}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
          Cancel
        </button>
      </div>

      {error && <div className="text-danger mt-2">{error.message}</div>}
    </form>
  );
}


function AddUserForm({ onSuccess, onClose }) {
  return (
    <ModalForm
      title="Add User"
      onClose={onClose}
    >
      <UserForm
        mutation={ADD_USER}
        onSuccess={onSuccess}
        onClose={onClose}
        buttonLabel="Add User"
      />
    </ModalForm>
  );
}

function EditUserForm({ userData, onSuccess, onClose }) {
  console.log("EditUserForm received userData:", userData);
  return (
    <ModalForm
      title="Edit User"
      onClose={onClose}
    >
      <UserForm
        mutation={UPDATE_USER}
        initialData={{ ...userData }}
        onSuccess={onSuccess}
        onClose={onClose}
        buttonLabel="Save Changes"
      />
    </ModalForm>
  );
}

export { AddUserForm, EditUserForm };
